import { createSignal } from "solid-js";

/**
 * Theme preference (FLOW-783). "system" follows prefers-color-scheme live;
 * an explicit choice is mirrored into localStorage. The only thing the
 * theme changes is the `dark` class on <html>: the Graphite tokens in
 * globals.css carry every colour, so no component needs a `dark:` variant.
 * public/theme.js applies the same class before first paint; this module
 * is the source of truth once the app runs.
 */
export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";
export const THEME_STORAGE_KEY = "flowicity_theme";
const QUERY = "(prefers-color-scheme: dark)";
const META_COLOR: Record<ResolvedTheme, string> = {
  light: "#f5f4f0",
  dark: "#121211",
};

function read(): ThemePreference {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return v === "light" || v === "dark" ? v : "system";
  } catch {
    return "system";
  }
}

const media = window.matchMedia(QUERY);
const [themePreference, setPreference] = createSignal<ThemePreference>(read());
const [osDark, setOsDark] = createSignal(media.matches);

const resolvedTheme = (): ResolvedTheme => {
  const p = themePreference();
  return p === "system" ? (osDark() ? "dark" : "light") : p;
};
const logoSrc = (): string =>
  resolvedTheme() === "dark" ? "/logo-black.png" : "/logo.png";

function apply(): void {
  const t = resolvedTheme();
  document.documentElement.classList.toggle("dark", t === "dark");
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", META_COLOR[t]);
}

export function setThemePreference(p: ThemePreference): void {
  setPreference(p);
  try {
    if (p === "system") localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, p);
  } catch {
    // Storage unavailable — the in-memory choice still applies.
  }
  apply();
}

media.addEventListener("change", (e) => {
  setOsDark(e.matches);
  apply();
});
apply();

export { themePreference, resolvedTheme, logoSrc };
